import React, { useState, useEffect } from 'react';
import api from '../api/client';
import siteData from '../data/siteData.json';
import './Contact.css';

const tabs = [
  { id: 'inquiry', label: '온라인 견적·상담 신청' },
  { id: 'notice', label: '공지사항' },
  { id: 'faq', label: '자주 묻는 질문' },
];

export default function Contact() {
  const [activeTab, setActiveTab] = useState('inquiry');
  const [company, setCompany] = useState(siteData.company);

  useEffect(() => {
    api.get('/company').then(setCompany).catch(() => {});
  }, []);

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <h1>고객센터</h1>
          <p>빈틈없는 공정 관리를 위한 제조업 도급부터, 단 하루의 완벽한 진행을 위한 행사운영 및 인력 대행까지.</p>
          <div className="breadcrumb">
            <span>HOME</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            <span className="current">고객센터</span>
          </div>
        </div>
      </div>

      <div className="about-tabs">
        <div className="container">
          <div className="tab-list">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`tab-btn ${activeTab === t.id ? 'tab-btn--active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {activeTab === 'inquiry' && <InquirySection company={company} />}
          {activeTab === 'notice' && <NoticeSection />}
          {activeTab === 'faq' && <FaqSection />}
        </div>
      </section>
    </main>
  );
}

function getContactSections(company) {
  const phone = company.phone || '';
  const fax = company.fax || '';
  return [
    {
      id: 'manufacturing',
      icon: '⚙️',
      title: '제조업 도급 / 아웃소싱·물류 담당',
      items: [
        { label: '전화', value: phone, href: phone ? `tel:${phone}` : undefined },
        { label: 'FAX', value: fax },
      ],
    },
    {
      id: 'events',
      icon: '🎉',
      title: '행사·이벤트 / 단기 인력 대행 담당',
      items: [
        { label: '전화', value: phone, href: phone ? `tel:${phone}` : undefined },
        { label: 'FAX', value: fax },
      ],
    },
  ];
}

/* ── Inquiry / Quote Form ── */
function InquirySection({ company }) {
  const email = company.email || 'hub_0530@naver.com';
  const hours = company.businessHours || company.business_hours || '평일 09:00 ~ 18:00';
  const contactSections = getContactSections(company);

  const [form, setForm] = useState({
    company: '', name: '', phone: '', email: '',
    serviceType: '행사 지원', headcount: '', message: '', agree: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.phone || !form.email) {
      alert('필수 항목을 모두 입력해 주세요.');
      return;
    }
    if (!form.agree) {
      alert('개인정보 수집 및 이용에 동의해 주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/inquiries', { ...form, source: 'quote' });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || '견적·상담 신청 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="inquiry-success">
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <h3>견적·상담 신청이 완료되었습니다</h3>
        <p>전담 컨설턴트가 1 영업일 이내에 입력하신 연락처로 안내드리겠습니다.</p>
        <button className="btn btn-primary" onClick={() => setSubmitted(false)}>새 문의 작성하기</button>
      </div>
    );
  }

  return (
    <div className="inquiry-wrapper">
      <div className="inquiry-info">
        <div className="contact-company-desc">
          <p><strong>[주식회사 허브]</strong>는 원청 기업의 경영 리스크를 최소화하고 효율성을 극대화하는 최고의 비즈니스 파트너입니다. 철저한 노무 준수, 신속한 인력 수급, 현장 맞춤형 관리 시스템으로 기업의 고민을 해결해 드립니다. 지금 전문가와 상담해 보세요.</p>
        </div>
        <h2>담당자 연락처</h2>
        <p className="text-muted">업무 분야별 담당자에게 직접 문의하세요.</p>
        <div className="contact-tabs-horizontal">
          {contactSections.map(sec => (
            <div key={sec.id} className="contact-tab-card">
              <div className="contact-tab-card__header">
                <span>{sec.icon}</span>
                <span>{sec.title}</span>
              </div>
              <div className="contact-tab-card__body">
                {sec.items.map(item => (
                  <div key={item.label} className="contact-accordion-row">
                    <span className="contact-accordion-label">{item.label}</span>
                    {item.href
                      ? <a href={item.href} className="contact-accordion-value">{item.value}</a>
                      : <span className="contact-accordion-value">{item.value}</span>
                    }
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="contact-common-info">
          <div className="contact-common-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <a href={`mailto:${email}`}>{email}</a>
          </div>
          <div className="contact-common-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>{hours}</span>
          </div>
        </div>
      </div>

      <form className="inquiry-form" onSubmit={handleSubmit}>
        <div className="inquiry-form-title">
          <span>📋</span>
          <span>1:1 맞춤형 아웃소싱 견적 및 전문 상담신청</span>
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="form-row-2">
          <div className="form-group">
            <label>회사명 <span className="required">*</span></label>
            <input name="company" value={form.company} onChange={handleChange} placeholder="주식회사 OO" />
          </div>
          <div className="form-group">
            <label>담당자명 <span className="required">*</span></label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="홍길동" />
          </div>
        </div>
        <div className="form-row-2">
          <div className="form-group">
            <label>연락처 <span className="required">*</span></label>
            <input name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" />
          </div>
          <div className="form-group">
            <label>이메일 <span className="required">*</span></label>
            <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="example@company.com" />
          </div>
        </div>
        <div className="form-row-2">
          <div className="form-group">
            <label>희망 서비스</label>
            <select name="serviceType" value={form.serviceType} onChange={handleChange}>
              <option>행사 지원</option>
              <option>헤드헌팅 및 채용대행</option>
              <option>생산·물류·도급</option>
              <option>아웃소싱 서비스 (기타)</option>
            </select>
          </div>
          <div className="form-group">
            <label>필요 인원</label>
            <input name="headcount" value={form.headcount} onChange={handleChange} placeholder="예: 5명" />
          </div>
        </div>
        <div className="form-group">
          <label>문의 내용</label>
          <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="필요하신 인력이나 업무에 대해 자세히 적어주시면 더 빠른 상담이 가능합니다." />
        </div>
        <label className="checkbox-row">
          <input type="checkbox" name="agree" checked={form.agree} onChange={handleChange} style={{ width: 'auto' }} />
          <span>개인정보 수집 및 이용에 동의합니다. <span className="required">*</span></span>
        </label>
        <button type="submit" className="btn btn-accent" style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }} disabled={submitting}>
          {submitting ? '신청 중...' : '견적·상담 신청하기'}
        </button>
      </form>
    </div>
  );
}

/* ── Notices ── */
function NoticeSection() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get('/notices')
      .then(setNotices)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="state-message">공지사항을 불러오는 중입니다...</div>;

  return (
    <div>
      <h2 className="list-section-title">공지사항</h2>
      {notices.length === 0 ? (
        <div className="state-message">등록된 공지사항이 없습니다.</div>
      ) : (
        <div className="notice-list">
          {notices.map(n => (
            <div key={n.id} className="notice-item" onClick={() => setOpenId(openId === n.id ? null : n.id)}>
              <div className="notice-item__header">
                <div className="notice-item__title-row">
                  {n.isPinned && <span className="badge badge-navy">필독</span>}
                  <span className="notice-item__title">{n.title}</span>
                </div>
                <div className="notice-item__right">
                  <span className="notice-item__date">{String(n.created_at).slice(0, 10)}</span>
                  <svg className={`chevron ${openId === n.id ? 'chevron--open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
              {openId === n.id && <div className="notice-item__content">{n.content}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── FAQ ── */
function FaqSection() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('전체');

  useEffect(() => {
    api.get('/faqs')
      .then(setFaqs)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="state-message">FAQ를 불러오는 중입니다...</div>;

  const categories = ['전체', ...new Set(faqs.map(f => f.category))];
  const filtered = filter === '전체' ? faqs : faqs.filter(f => f.category === filter);

  return (
    <div>
      <h2 className="list-section-title">자주 묻는 질문</h2>
      <div className="type-filter" style={{ marginBottom: '1.5rem' }}>
        {categories.map(c => (
          <button
            key={c}
            className={`type-filter__btn ${filter === c ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(c)}
          >
            {c}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="state-message">등록된 FAQ가 없습니다.</div>
      ) : (
        <div className="faq-list">
          {filtered.map(f => (
            <div key={f.id} className="faq-item" onClick={() => setOpenId(openId === f.id ? null : f.id)}>
              <div className="faq-item__q">
                <span className="faq-q-mark">Q</span>
                <span>{f.question}</span>
                <svg className={`chevron ${openId === f.id ? 'chevron--open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
              {openId === f.id && (
                <div className="faq-item__a">
                  <span className="faq-a-mark">A</span>
                  <span>{f.answer}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
